import React, { useState, useEffect } from 'react';
import './FileManager.css';
import Icon from './Icon';
import {
  listOwned, addFile, updateFile, removeFile, hydrateFiles, fileDataUrl, flushTitleSave,
  prepareUpload, isSupportedFile, isImageFile, fmtSize, MAX_FILE_BYTES, FILE_ACCEPT,
  loadHidden, archiveFile, unarchiveFile, openFile, setFileRelay,
} from './fileLib';

// Manage the files OWNED at one level (upload / rename / public-private / delete) and,
// optionally, show the PUBLIC files INHERITED from parent levels as read-only.
// `ownerKey` is where new uploads are stored (e.g. 'mls:<id>' | 'org:<id>' | 'agent:<id>').
// Uploads are auto-sorted into two sections by type: documents (Files) and images
// (Graphs & Images) — same inheritance / view / archive machinery in both. An agent's
// Public toggle shows a document in the profile's "Market Reports & Stats" and an
// image in the profile's "Charts & Statistics".
function FileManager({ ownerKey, inherited = [], compact = false, profileFiles, onToggleProfile }) {
  // When onToggleProfile is provided (the agent's own Files screen), each file gets a
  // "Public" toggle = show it in the agent's public profile. This is separate from the
  // MLS/org "Public" cascade below.
  const canProfile = typeof onToggleProfile === 'function';
  const onProfile = (id) => Array.isArray(profileFiles) && profileFiles.includes(id);
  const [owned, setOwned] = useState(() => listOwned(ownerKey));
  const [err, setErr]     = useState('');
  const [busy, setBusy]   = useState(false);
  const [hidden, setHidden] = useState(() => loadHidden(ownerKey));
  const [showArchived, setShowArchived] = useState(false);
  const [relayOverlay, setRelayOverlay] = useState({});   // fileId -> bool, reflects toggles before parent re-renders

  // Only a level WITH a level below it can set visibility (MLS → orgs, org → agents).
  // An agent is the bottom of the tree, so it gets no Public/Private control at all.
  const canRelay = !String(ownerKey || '').startsWith('agent:');

  // Sort within each group: newest-uploaded first (default) or by name.
  const [sortBy, setSortBy] = useState('newest');   // 'newest' | 'name'
  const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : '');   // YYYY-MM-DD (upload date)
  const sortFiles = (list) => [...list].sort((a, b) => (sortBy === 'name'
    ? String(a.name || '').localeCompare(String(b.name || ''))
    : String(b.createdAt || '').localeCompare(String(a.createdAt || ''))));

  const refresh = () => setOwned(listOwned(ownerKey));
  const refreshHidden = () => setHidden(loadHidden(ownerKey));

  // Pull the latest library metadata from the server on mount, so files uploaded
  // from ANOTHER browser/level appear without a full reload.
  useEffect(() => {
    let alive = true;
    hydrateFiles().then(() => { if (alive) setOwned(listOwned(ownerKey)); });
    return () => { alive = false; };
  }, [ownerKey]);
  function archive(id) { archiveFile(ownerKey, id); refreshHidden(); }
  function unarchive(id) { unarchiveFile(ownerKey, id); refreshHidden(); }
  // Whether THIS level relays an inherited file on to its own children.
  const isRelayed = (f) => (f.id in relayOverlay) ? relayOverlay[f.id] : !!(f.relays || {})[ownerKey];
  function toggleRelay(f) {
    const next = !isRelayed(f);
    setFileRelay(f.id, ownerKey, next);
    setRelayOverlay(prev => ({ ...prev, [f.id]: next }));
  }

  const hiddenSet = new Set(hidden);
  const visibleInherited = inherited.filter(f => !hiddenSet.has(f.id));
  const archivedInherited = inherited.filter(f => hiddenSet.has(f.id));

  async function onPick(file) {
    setErr('');
    if (!file) return;
    if (!isSupportedFile(file)) { setErr('Only PDF, .txt, and image (JPG/PNG/WebP/GIF) files are supported.'); return; }
    setBusy(true);
    try {
      // Images are auto-compressed (≤1400px JPEG) so a phone photo doesn't blow the
      // storage quota; the size cap applies to what's actually STORED.
      const { data, size } = await prepareUpload(file);
      if (size > MAX_FILE_BYTES) {
        setErr(`"${file.name}" is ${fmtSize(size)}${size !== file.size ? ' after compression' : ''} — over the ${fmtSize(MAX_FILE_BYTES)} limit.`);
        return;
      }
      await addFile({ owner: ownerKey, name: file.name, type: file.type, size, data, isPublic: false });   // starts Private — share explicitly
      refresh();
    } catch (e) {
      setErr(/quota/i.test(e && e.message) ? 'Storage is full — remove a file and try again.' : (e.message || 'Upload failed.'));
    } finally { setBusy(false); }
  }

  // Edit a file's descriptive TITLE (the link text). The filename (name) is left alone.
  function setTitleLocal(id, title) {
    setOwned(prev => prev.map(f => f.id === id ? { ...f, title } : f));
    updateFile(id, { title });
  }
  const titleOf = (f) => (f.title != null && f.title !== '') ? f.title : f.name;
  function togglePublic(id, cur) { updateFile(id, { public: !cur }); refresh(); }
  function del(id) { removeFile(id); refresh(); }

  // Images show a real thumbnail (the bytes are right here as a data URL); documents
  // get the generic file icon.
  const icon = f => isImageFile(f)
    ? <img className="fm-thumb" src={f.data || fileDataUrl(f.id)} alt="" loading="lazy" />
    : <Icon name="files" size={16} />;

  // Where does the agent-profile Public toggle surface this file?
  const profileSection = (f) => isImageFile(f) ? 'Charts & Statistics' : 'Market Reports';

  const ownedRow = (f) => (
    <li key={f.id} className="fm-item">
      <span className="fm-date" title="Uploaded (YYYY-MM-DD)">{fmtDate(f.createdAt)}</span>
      <span className="fm-icon">{icon(f)}</span>
      <span className="fm-titlecell">
        <input className="fm-name" value={f.title != null ? f.title : ''} onChange={e => setTitleLocal(f.id, e.target.value)}
          title="Title — the descriptive link text shown on your public profile" placeholder="Add a descriptive title…"
          onFocus={e => e.target.select()} onBlur={() => flushTitleSave(f.id)} />
        <span className="fm-filename" title="Uploaded filename">{f.name}</span>
      </span>
      <span className="fm-size">{fmtSize(f.size)}</span>
      <button className="fm-view" title="View the file content in a new tab" onClick={() => openFile(f)}><Icon name="view" size={16} /> View</button>
      {canProfile && (
        <button className={`fm-flag ${onProfile(f.id) ? 'on' : 'off'}`} onClick={() => onToggleProfile(f.id, !onProfile(f.id))}
          title={onProfile(f.id) ? `Public — shown in your public profile’s ${profileSection(f)}. Click to hide.` : `Private — not on your public profile. Click to show it in ${profileSection(f)}.`}>
          {onProfile(f.id) ? <><Icon name="public" size={16} /> Public</> : <><Icon name="private" size={16} /> Private</>}
        </button>
      )}
      {canRelay && (
        <button className={`fm-flag ${f.public ? 'on' : 'off'}`} onClick={() => togglePublic(f.id, f.public)}
          title={f.public ? 'Public — shared (read-only) with the level below. Click to make private.' : 'Private — only this level. Click to share with the level below.'}>
          {f.public ? <><Icon name="public" size={16} /> Public</> : <><Icon name="private" size={16} /> Private</>}
        </button>
      )}
      <button className="fm-del" title="Delete file" onClick={() => del(f.id)}><Icon name="delete" size={16} /></button>
    </li>
  );

  const inheritedRow = (f) => (
    <li key={f.id} className="fm-item fm-item-ro">
      <span className="fm-date" title="Uploaded (YYYY-MM-DD)">{fmtDate(f.createdAt)}</span>
      <span className="fm-icon">{icon(f)}</span>
      <span className="fm-titlecell">
        <span className="fm-name fm-name-ro">{titleOf(f)}</span>
        <span className="fm-filename" title="Uploaded filename">{f.name}</span>
      </span>
      <span className="fm-size">{fmtSize(f.size)}</span>
      <span className="fm-from">{f.owner.startsWith('mls:') ? 'from MLS' : f.owner.startsWith('org:') ? 'from Org' : ''}</span>
      <button className="fm-view" title="View the file content in a new tab" onClick={() => openFile(f)}><Icon name="view" size={16} /> View</button>
      {canProfile && (
        <button className={`fm-flag ${onProfile(f.id) ? 'on' : 'off'}`} onClick={() => onToggleProfile(f.id, !onProfile(f.id))}
          title={onProfile(f.id) ? `Public — shown in your public profile’s ${profileSection(f)}. Click to hide.` : `Private — not on your public profile. Click to show it in ${profileSection(f)}.`}>
          {onProfile(f.id) ? <><Icon name="public" size={16} /> Public</> : <><Icon name="private" size={16} /> Private</>}
        </button>
      )}
      {canRelay && (
        <button className={`fm-flag ${isRelayed(f) ? 'on' : 'off'}`} onClick={() => toggleRelay(f)}
          title={isRelayed(f) ? 'Public — passed on (read-only) to the level below. Click to stop sharing it down.' : 'Private — the level below cannot see this. Click to pass it on.'}>
          {isRelayed(f) ? <><Icon name="public" size={16} /> Public</> : <><Icon name="private" size={16} /> Private</>}
        </button>
      )}
      <button className="fm-archive" title="Archive — hide from your lists" onClick={() => archive(f.id)}>Archive</button>
    </li>
  );

  // One section = your files of a kind + the inherited files of the same kind.
  // Documents and images get identical treatment; only the grouping differs.
  const section = (title, iconName, ownFiles, inhFiles, emptyText) => (
    <>
      <div className="fm-section-head"><Icon name={iconName} size={19} /> {title} <span className="fm-rename-hint">— tap a title to edit it</span></div>
      {ownFiles.length === 0 ? (
        <div className="fm-empty">{emptyText}</div>
      ) : (
        <ul className="fm-list">{sortFiles(ownFiles).map(ownedRow)}</ul>
      )}
      {inhFiles.length > 0 && (
        <div className="fm-inherited">
          <div className="fm-inherited-title fm-section-title"><Icon name="organizations" size={18} /> INHERITED (READ-ONLY)</div>
          <ul className="fm-list">{sortFiles(inhFiles).map(inheritedRow)}</ul>
        </div>
      )}
    </>
  );

  const ownedDocs = owned.filter(f => !isImageFile(f));
  const ownedImgs = owned.filter(isImageFile);
  const inhDocs = visibleInherited.filter(f => !isImageFile(f));
  const inhImgs = visibleInherited.filter(isImageFile);

  return (
    <div className={`fm ${compact ? 'fm-compact' : ''}`}>
      <div className="fm-bar">
        <label className="fm-upload">
          {busy ? 'Uploading…' : <><Icon name="upload" size={16} /> Upload file</>}
          <input type="file" accept={FILE_ACCEPT} disabled={busy} style={{ display: 'none' }}
            onChange={e => { onPick(e.target.files && e.target.files[0]); e.target.value = ''; }} />
        </label>
        <span className="fm-hint">PDF, .txt, or image (JPG/PNG/WebP/GIF), up to {fmtSize(MAX_FILE_BYTES)} — big images are compressed automatically. Documents land in Files, images in Graphs &amp; Images. Click a title to edit it — it's the link text shown everywhere{canProfile ? ', including your public profile' : ''}. {canProfile ? <>Mark one <strong>Public</strong> to show it on your profile — documents under <strong>Market Reports</strong>, images under <strong>Charts &amp; Statistics</strong>.</> : <>Files start <strong>Private</strong>{canRelay ? ' — mark one Public to share it (read-only) with the level below.' : '.'}</>}</span>
        <select className="fm-sort" value={sortBy} onChange={e => setSortBy(e.target.value)} title="Sort files" aria-label="Sort files">
          <option value="newest">Newest first</option>
          <option value="name">Name (A–Z)</option>
        </select>
      </div>
      {err && <div className="fm-error">⚠ {err}</div>}

      {section('Your files', 'folder', ownedDocs, inhDocs, 'No files uploaded at this level yet.')}
      {section('Graphs & Images', 'view', ownedImgs, inhImgs, 'No images uploaded at this level yet.')}

      {archivedInherited.length > 0 && (
        <div className="fm-archived">
          <button className="fm-archived-toggle" onClick={() => setShowArchived(s => !s)}>
            {showArchived ? '▾' : '▸'} Archived ({archivedInherited.length})
          </button>
          {showArchived && (
            <ul className="fm-list">
              {sortFiles(archivedInherited).map(f => (
                <li key={f.id} className="fm-item fm-item-ro fm-item-archived">
                  <span className="fm-icon">{icon(f)}</span>
                  <span className="fm-name fm-name-ro">{titleOf(f)}</span>
                  <span className="fm-date" title="Uploaded">{fmtDate(f.createdAt)}</span>
                  <span className="fm-size">{fmtSize(f.size)}</span>
                  <button className="fm-view" title="View the file content in a new tab" onClick={() => openFile(f)}><Icon name="view" size={16} /> View</button>
                  <button className="fm-archive fm-restore" title="Restore to your lists" onClick={() => unarchive(f.id)}>Restore</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default FileManager;
